import { ABORT_ERROR } from './abortControl';
import type { Cleanable } from './cleanable';
import { makeError } from './exception';

type OnChange<T> = (newValue: T) => void;

export interface Observable<T> {
  /**
   * Registers the given listener to be notified on changes.
   * Returns a Cleanable object to unregister the listener.
   */
  observe(onChange: (newValue: T) => void): Cleanable;

  currentValue(): T;

  waitForChange(signal?: AbortSignal): Promise<T>;
}


/**
 * This class is a plain typescript implementation of an observable mechanism.
 * The owner of an ObservableState foo can update its value with foo.value = ...
 * but the observers can only read the value and be notified when it changes.
 *
 * This class is designed to be used in situations where we want some Vue-free code
 * to be able to expose its values in a readonly way so that it is possible to build
 * a reactive UI without having to make the values themselves reactive (like for
 * instance by putting them in the store).
 */
export class ObservableState<T> implements Observable<T> {

  private listeners: OnChange<T>[] = [];
  private _value: T;

  constructor(v: T) {
    this._value = v;
  }

  public get value(): T {
    return this._value;
  }

  public set value(v: T) {
    if (this._value === v) {
      return;
    }
    this._value = v;
    for (const onChange of this.listeners) {
      onChange(v);
    }
  }

  getObservable(): Observable<T> {
    return {
      observe: (onChange: (newValue: T) => void): Cleanable => {
        this.addListener(onChange);
        const cleanup = () => { this.removeListener(onChange); };
        return {
          cleanup,
        };
      },
      currentValue: () => this._value,
      waitForChange: async (abort?: AbortSignal): Promise<T> => {
        const initialValue = this._value;
        return new Promise<T>((resolve, reject) => {
          const onAbort = () => {
            this.removeListener(onChange);
            abort?.removeEventListener('abort', onAbort);
            reject(makeError('Aborted', ABORT_ERROR));
          };

          const onChange = (value: T) => {
            if (value !== initialValue) {
              this.removeListener(onChange);
              abort?.removeEventListener('abort', onAbort);
              resolve(value);
            }
          };

          abort?.addEventListener('abort', onAbort);
          this.addListener(onChange);
        });
      }
    };
  }

  observe(onChange: (newValue: T) => void): Cleanable {
    return this.getObservable().observe(onChange);
  }

  currentValue(): T {
    return this.value;
  }

  async waitForChange(signal?: AbortSignal): Promise<T> {
    return await this.getObservable().waitForChange(signal);
  }

  // Note: Only used by unit tests to confirm cleanup
  public listenerCount(): number {
    return this.listeners.length;
  }

  /**
   * Adds a listener and invoke immediately with the current value
   */
  private addListener(onChange: OnChange<T>) {
    this.listeners.push(onChange);
    onChange(this._value);
  }

  private removeListener(l: OnChange<T>) {
    const index = this.listeners.indexOf(l);
    if (index !== -1) {
      this.listeners.splice(index, 1);
    }
  }
}

export function calculatedObservable<Inputs extends any[], Outputs>(
  f: (...inputs: Inputs) => Outputs,
  cleanables: Cleanable[],
  ...observables: { [K in keyof Inputs]: Observable<Inputs[K]> | ObservableState<Inputs[K]>}
) {
  const initialValues = observables.map(obs => obs.currentValue()) as Inputs;
  const result = new ObservableState<Outputs>(f(...initialValues));

  for (let inputIndex = 0; inputIndex < observables.length; inputIndex++) {
    cleanables.push(observables[inputIndex]!.observe((newValue: any) => {
      initialValues[inputIndex] = newValue;
      result.value = f(...initialValues);
    }));
  }

  return result.getObservable();
}


